import { IDateRange, IInvoicesHook } from '@models';
import { InvoiceService } from '@services';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useInvoices = (): IInvoicesHook => {
  const { invoiceStore } = useStore();
  const {
    loading,
    invoices,
    totalCount,
    skipCount,
    maxResultCount,
    currentPage,
    setSkipCount,
    setCurrentPage,
  } = invoiceStore;

  const [number, setNumber] = useState<string>();
  const [customer, setCustomer] = useState<string>();
  const [registrationNumber, setRegistrationNumber] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [dateRange, setDateRange] = useState<IDateRange>();
  const [datePreset, setDatePreset] = useState<string | undefined>();

  const presetRange = useMemo(() => getDateRangeFromPreset(datePreset), [datePreset]);

  useEffect(() => {
    return () => invoiceStore.resetInvoices();
  }, [invoiceStore]);

  useEffect(() => {
    const startAt = datePreset
      ? presetRange.startAt
      : dateRange && dayjs(dateRange.startAt).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endAt = datePreset
      ? presetRange.endAt
      : dateRange && dayjs(dateRange.endAt).endOf('day').format('YYYY-MM-DD HH:mm:ss');

    invoiceStore.getAll({
      customer,
      status,
      number,
      registrationNumber,
      startAt,
      endAt,
      maxResultCount,
      skipCount,
    });
  }, [
    invoiceStore,
    customer,
    status,
    number,
    registrationNumber,
    dateRange,
    datePreset,
    presetRange,
    maxResultCount,
    skipCount,
  ]);

  const handlePageChange = (pagination: any) => {
    invoiceStore.setMaxResultCount(pagination.pageSize);
    invoiceStore.setSkipCount((pagination.current - 1) * pagination.pageSize);
    invoiceStore.setCurrentPage(pagination.current);
  };

  const handleNumberSearch = debounce((text: string) => {
    setNumber(text.trim() || undefined);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleCustomerSearch = debounce((text: string) => {
    setCustomer(text.trim() || undefined);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleRegistrationNumberSearch = debounce((text: string) => {
    setRegistrationNumber(text.trim() || undefined);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleDateRangeChange = (range: IDateRange | undefined) => {
    setDatePreset(undefined);
    setDateRange(range);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleDatePresetChange = (preset: string) => {
    setDateRange(undefined);
    setDatePreset(preset || undefined);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const startAt = datePreset
      ? presetRange.startAt
      : dateRange && dayjs(dateRange.startAt).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endAt = datePreset
      ? presetRange.endAt
      : dateRange && dayjs(dateRange.endAt).endOf('day').format('YYYY-MM-DD HH:mm:ss');

    const result = await InvoiceService.getAll({
      customer,
      status,
      number,
      registrationNumber,
      startAt,
      endAt,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
    });
    return result.items;
  }, [customer, status, number, registrationNumber, dateRange, datePreset, presetRange, totalCount]);

  return {
    loading,
    dataSource: Array.from(invoices.values()),
    totalCount,
    maxResultCount,
    handleNumberSearch,
    handleCustomerSearch,
    handleRegistrationNumberSearch,
    currentPage,
    setStatus,
    datePreset,
    setDateRange: handleDateRangeChange,
    handleDatePresetChange,
    handlePageChange,
    fetchAllForExport,
  };
};
